import React, { useState, useEffect, useCallback } from 'react';
import { SettingsPage } from '../../components/SettingsPage/SettingsPage';
import { ToggleSettingCard } from '../../components/SettingCards/ToggleSetting/ToggleSetting';
import { SettingCard } from '../../components/SettingCards/SettingCard/SettingCard';
import { TextInput } from '../../components/TextInput/TextInput';
import { SHORTCUT_SETTING_IDS } from '../../config/SettingIds';
import './ShortcutSettingsPage.css';
import {
	getSettingConfig,
	TextInputSettingConfig,
	ToggleSettingConfig,
} from '../../config/SettingsDefinitions';
import { SettingsNativeBridgeFactory } from '../../bridge/SettingsNativeBridgeFactory';
import { ToggleSettingGroup } from '../../components/SettingCards/ToggleSetting/ToggleSettingGroup';
import { ToggleSettingGroupConfig } from '../../config/SettingsDefinitions';
import { getFilteredSettingIds } from '../../config/DynamicSettingsFilter';
import { SettingsCategory } from '../../config/SettingsCategory';
import { SETTING_NOTE_MESSAGES } from '../../components/SettingCards/SettingCard/SettingStatus/SettingStatus';

interface StartMenuDirectorySetting {
	directoryPath: string;
	showAppsInStartMenu: boolean;
}

interface DesktopDirectorySetting {
	directoryPath: string;
	showAppsOnDesktop: boolean;
}

interface OrganizeByCategoriesSetting {
	separateFoldersInStartMenu: boolean;
	separateFoldersInDesktop: boolean;
}

interface ShortcutSettings {
	startMenuDirectory: StartMenuDirectorySetting;
	desktopDirectory: DesktopDirectorySetting;
	organizeByCategories: OrganizeByCategoriesSetting;
}

interface ShortcutSettingsEnabled {
	startMenuDirectory: boolean;
	desktopDirectory: boolean;
	organizeByCategories: boolean;
}

interface ShortcutSettingsPageProps {
	title: string;
	onNavigatingBack: () => void;
}

export const ShortcutSettingsPage: React.FC<ShortcutSettingsPageProps> = ({
	title,
	onNavigatingBack,
}) => {
	// State for settings
	const [settings, setSettings] = useState<ShortcutSettings>({
		startMenuDirectory: {
			directoryPath: '',
			showAppsInStartMenu: false,
		},
		desktopDirectory: {
			directoryPath: '',
			showAppsOnDesktop: false,
		},
		organizeByCategories: {
			separateFoldersInStartMenu: false,
			separateFoldersInDesktop: false,
		},
	});

	// State for enabled/disabled status
	const [settingsEnabled, setSettingsEnabled] = useState<ShortcutSettingsEnabled>({
		startMenuDirectory: true,
		desktopDirectory: true,
		organizeByCategories: true,
	});

	const [error, setError] = useState<string | null>(null);

	// Debounce timers for text input saving
	const [startMenuDebounceTimer, setStartMenuDebounceTimer] =
		useState<NodeJS.Timeout | null>(null);
	const [desktopDebounceTimer, setDesktopDebounceTimer] = useState<NodeJS.Timeout | null>(
		null
	);

	useEffect(() => {
		loadShortcutSettings();
		// Cleanup timers on unmount
		return () => {
			if (startMenuDebounceTimer) clearTimeout(startMenuDebounceTimer);
			if (desktopDebounceTimer) clearTimeout(desktopDebounceTimer);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const loadShortcutSettings = async () => {
		try {
			const bridge = SettingsNativeBridgeFactory.getBridge();

			// Get platform and entrypoint from bridge
			const platform = bridge.getPlatform();
			const entrypoint = bridge.getEntrypoint();

			// Get filtered setting IDs for Shortcuts category
			const filteredIds = getFilteredSettingIds({
				category: SettingsCategory.Shortcuts,
				platform,
				entrypoint,
			});

			// Build settings request only for available settings
			const settingsToLoad = [
				SHORTCUT_SETTING_IDS.START_MENU_DIRECTORY_SETTING,
				SHORTCUT_SETTING_IDS.DESKTOP_DIRECTORY_SETTING,
				SHORTCUT_SETTING_IDS.ORGANIZE_START_MENU_CATEGORIES,
				SHORTCUT_SETTING_IDS.ORGANIZE_DESKTOP_CATEGORIES,
			].filter((settingId) => filteredIds.includes(settingId));

			if (settingsToLoad.length === 0) {
				return;
			}

			const settingsResponse = await bridge.getSettings({
				settings: settingsToLoad.map((settingKey) => ({ settingKey })),
			});

			if (settingsResponse && settingsResponse.data && settingsResponse.data.settings) {
				const settingsData = settingsResponse.data.settings.reduce(
					(acc, setting) => {
						acc[setting.settingKey] = setting;
						return acc;
					},
					{} as Record<
						string,
						{ settingKey: string; settingValue: unknown; enabled?: boolean }
					>
				);

				// Parse start menu directory settings
				let startMenuDirectory: StartMenuDirectorySetting;
				try {
					const startMenuData =
						settingsData[SHORTCUT_SETTING_IDS.START_MENU_DIRECTORY_SETTING];
					if (startMenuData && typeof startMenuData.settingValue === 'string') {
						startMenuDirectory = JSON.parse(startMenuData.settingValue as string);
					} else {
						startMenuDirectory = {
							directoryPath: '',
							showAppsInStartMenu: false,
						};
					}
				} catch (e) {
					startMenuDirectory = {
						directoryPath: '',
						showAppsInStartMenu: false,
					};
				}

				// Parse desktop directory settings
				let desktopDirectory: DesktopDirectorySetting;
				try {
					const desktopData =
						settingsData[SHORTCUT_SETTING_IDS.DESKTOP_DIRECTORY_SETTING];
					if (desktopData && typeof desktopData.settingValue === 'string') {
						desktopDirectory = JSON.parse(desktopData.settingValue as string);
					} else {
						desktopDirectory = {
							directoryPath: '',
							showAppsOnDesktop: false,
						};
					}
				} catch (e) {
					desktopDirectory = {
						directoryPath: '',
						showAppsOnDesktop: false,
					};
				}

				// Parse organize by categories settings from individual subsettings
				let organizeByCategories: OrganizeByCategoriesSetting;
				try {
					const startMenuCategoriesData =
						settingsData[SHORTCUT_SETTING_IDS.ORGANIZE_START_MENU_CATEGORIES];
					const desktopCategoriesData =
						settingsData[SHORTCUT_SETTING_IDS.ORGANIZE_DESKTOP_CATEGORIES];

					organizeByCategories = {
						separateFoldersInStartMenu: startMenuCategoriesData
							? startMenuCategoriesData.settingValue === 'true'
							: false,
						separateFoldersInDesktop: desktopCategoriesData
							? desktopCategoriesData.settingValue === 'true'
							: false,
					};
				} catch (e) {
					organizeByCategories = {
						separateFoldersInStartMenu: false,
						separateFoldersInDesktop: false,
					};
				}

				const loadedSettings: ShortcutSettings = {
					startMenuDirectory,
					desktopDirectory,
					organizeByCategories,
				};

				const loadedEnabledStates: ShortcutSettingsEnabled = {
					startMenuDirectory:
						settingsData[SHORTCUT_SETTING_IDS.START_MENU_DIRECTORY_SETTING]?.enabled ??
						true,
					desktopDirectory:
						settingsData[SHORTCUT_SETTING_IDS.DESKTOP_DIRECTORY_SETTING]?.enabled ?? true,
					organizeByCategories:
						(settingsData[SHORTCUT_SETTING_IDS.ORGANIZE_START_MENU_CATEGORIES]?.enabled ??
							true) &&
						(settingsData[SHORTCUT_SETTING_IDS.ORGANIZE_DESKTOP_CATEGORIES]?.enabled ??
							true),
				};

				setSettings(loadedSettings);
				setSettingsEnabled(loadedEnabledStates);
			} else {
				setError('Failed to load shortcut settings');
			}
		} catch (err) {
			console.error('Error loading shortcut settings:', err);
			setError('Error loading shortcut settings');
		} finally {
		}
	};

	const saveStartMenuDirectorySettings = async (
		startMenuDirectory: StartMenuDirectorySetting
	) => {
		try {
			const bridge = SettingsNativeBridgeFactory.getBridge();
			await bridge.setSetting({
				settingKey: SHORTCUT_SETTING_IDS.START_MENU_DIRECTORY_SETTING,
				settingValue: JSON.stringify(startMenuDirectory),
			});
		} catch (err) {
			console.error('Error saving start menu directory settings:', err);
		}
	};

	const saveDesktopDirectorySettings = async (
		desktopDirectory: DesktopDirectorySetting
	) => {
		try {
			const bridge = SettingsNativeBridgeFactory.getBridge();
			await bridge.setSetting({
				settingKey: SHORTCUT_SETTING_IDS.DESKTOP_DIRECTORY_SETTING,
				settingValue: JSON.stringify(desktopDirectory),
			});
		} catch (err) {
			console.error('Error saving desktop directory settings:', err);
		}
	};

	const saveStartMenuCategoriesSetting = async (value: boolean) => {
		try {
			const bridge = SettingsNativeBridgeFactory.getBridge();
			await bridge.setSetting({
				settingKey: SHORTCUT_SETTING_IDS.ORGANIZE_START_MENU_CATEGORIES,
				settingValue: value.toString(),
			});
		} catch (err) {
			console.error('Error saving start menu categories setting:', err);
		}
	};

	const saveDesktopCategoriesSetting = async (value: boolean) => {
		try {
			const bridge = SettingsNativeBridgeFactory.getBridge();
			await bridge.setSetting({
				settingKey: SHORTCUT_SETTING_IDS.ORGANIZE_DESKTOP_CATEGORIES,
				settingValue: value.toString(),
			});
		} catch (err) {
			console.error('Error saving desktop categories setting:', err);
		}
	};

	const handleStartMenuEnabledChange = useCallback((value: boolean) => {
		setSettings((prev) => {
			const newStartMenuDirectory = {
				...prev.startMenuDirectory,
				showAppsInStartMenu: value,
			};
			saveStartMenuDirectorySettings(newStartMenuDirectory);
			return { ...prev, startMenuDirectory: newStartMenuDirectory };
		});
	}, []);

	const handleDesktopEnabledChange = useCallback((value: boolean) => {
		setSettings((prev) => {
			const newDesktopDirectory = { ...prev.desktopDirectory, showAppsOnDesktop: value };
			saveDesktopDirectorySettings(newDesktopDirectory);
			return { ...prev, desktopDirectory: newDesktopDirectory };
		});
	}, []);

	const handleStartMenuFolderChange = useCallback((value: string) => {
		setSettings((prev) => ({
			...prev,
			startMenuDirectory: { ...prev.startMenuDirectory, directoryPath: value },
		}));

		// Clear existing timer
		setStartMenuDebounceTimer((prevTimer) => {
			if (prevTimer) clearTimeout(prevTimer);
			return null;
		});

		// Set new timer
		const newTimer = setTimeout(() => {
			setSettings((current) => {
				const newStartMenuDirectory = {
					...current.startMenuDirectory,
					directoryPath: value,
				};
				saveStartMenuDirectorySettings(newStartMenuDirectory);
				return current;
			});
		}, 5000);

		setStartMenuDebounceTimer(newTimer);
	}, []);

	const handleDesktopFolderChange = useCallback((value: string) => {
		setSettings((prev) => ({
			...prev,
			desktopDirectory: { ...prev.desktopDirectory, directoryPath: value },
		}));

		// Clear existing timer
		setDesktopDebounceTimer((prevTimer) => {
			if (prevTimer) clearTimeout(prevTimer);
			return null;
		});

		// Set new timer
		const newTimer = setTimeout(() => {
			setSettings((current) => {
				const newDesktopDirectory = { ...current.desktopDirectory, directoryPath: value };
				saveDesktopDirectorySettings(newDesktopDirectory);
				return current;
			});
		}, 5000);

		setDesktopDebounceTimer(newTimer);
	}, []);

	const handleStartMenuCategoriesChange = useCallback((value: boolean) => {
		setSettings((prev) => ({
			...prev,
			organizeByCategories: {
				...prev.organizeByCategories,
				separateFoldersInStartMenu: value,
			},
		}));
		saveStartMenuCategoriesSetting(value);
	}, []);

	const handleDesktopCategoriesChange = useCallback((value: boolean) => {
		setSettings((prev) => ({
			...prev,
			organizeByCategories: {
				...prev.organizeByCategories,
				separateFoldersInDesktop: value,
			},
		}));
		saveDesktopCategoriesSetting(value);
	}, []);

	if (error) {
		return (
			<SettingsPage
				title={title}
				canNavigateBack={!!onNavigatingBack}
				onNavigatingBack={onNavigatingBack}
				settings={<div>Error: {error}</div>}
			/>
		);
	}
	const startMenuDirectorySettingConfig = getSettingConfig(
		SHORTCUT_SETTING_IDS.START_MENU_DIRECTORY_SETTING
	) as ToggleSettingConfig;
	const desktopDirectorySettingConfig = getSettingConfig(
		SHORTCUT_SETTING_IDS.DESKTOP_DIRECTORY_SETTING
	) as ToggleSettingConfig;

	const organizeByCategoriesConfig = getSettingConfig(
		SHORTCUT_SETTING_IDS.ORGANIZE_BY_CATEGORIES
	) as ToggleSettingGroupConfig;

	return (
		<SettingsPage
			title={title}
			canNavigateBack={!!onNavigatingBack}
			onNavigatingBack={onNavigatingBack}
			settings={
				<div className="shortcut-settings">
					<ToggleSettingCard
						settingId={SHORTCUT_SETTING_IDS.START_MENU_DIRECTORY_SETTING}
						title={startMenuDirectorySettingConfig.title}
						description={startMenuDirectorySettingConfig.description}
						checked={settings.startMenuDirectory.showAppsInStartMenu}
						onChange={handleStartMenuEnabledChange}
						disabled={!settingsEnabled.startMenuDirectory}
						warningCondition={() => true}
						warningMessage={SETTING_NOTE_MESSAGES.RESTART_WORKSPACE_APP}
					>
						<TextInput
							label={
								(
									startMenuDirectorySettingConfig
										.subSettings?.[0] as TextInputSettingConfig
								).label
							}
							showInfoIcon={true}
							tooltip={
								(
									startMenuDirectorySettingConfig
										.subSettings?.[0] as TextInputSettingConfig
								).helpText
							}
							value={settings.startMenuDirectory.directoryPath}
							onChange={handleStartMenuFolderChange}
							placeholder={
								(
									startMenuDirectorySettingConfig
										.subSettings?.[0] as TextInputSettingConfig
								).placeholder
							}
							disabled={
								!settings.startMenuDirectory.showAppsInStartMenu ||
								!settingsEnabled.startMenuDirectory
							}
						/>
					</ToggleSettingCard>

					<ToggleSettingCard
						settingId={SHORTCUT_SETTING_IDS.DESKTOP_DIRECTORY_SETTING}
						title={desktopDirectorySettingConfig.title}
						description={desktopDirectorySettingConfig.description}
						checked={settings.desktopDirectory.showAppsOnDesktop}
						onChange={handleDesktopEnabledChange}
						disabled={!settingsEnabled.desktopDirectory}
						warningCondition={() => true}
						warningMessage={SETTING_NOTE_MESSAGES.RESTART_WORKSPACE_APP}
					>
						<TextInput
							label={
								(desktopDirectorySettingConfig.subSettings?.[0] as TextInputSettingConfig)
									.label
							}
							showInfoIcon={true}
							value={settings.desktopDirectory.directoryPath}
							onChange={handleDesktopFolderChange}
							placeholder={
								(desktopDirectorySettingConfig.subSettings?.[0] as TextInputSettingConfig)
									.placeholder
							}
							tooltip={
								(desktopDirectorySettingConfig.subSettings?.[0] as TextInputSettingConfig)
									.helpText
							}
							disabled={
								!settings.desktopDirectory.showAppsOnDesktop ||
								!settingsEnabled.desktopDirectory
							}
						/>
					</ToggleSettingCard>

					<SettingCard
						settingId={SHORTCUT_SETTING_IDS.ORGANIZE_BY_CATEGORIES}
						title={organizeByCategoriesConfig.title}
						showContentSeparator={
							organizeByCategoriesConfig.showContentSeparator ?? false
						}
						disabled={!settingsEnabled.organizeByCategories}
						showStatusOnValueChange={true}
						statusMessage={SETTING_NOTE_MESSAGES.RESTART_WORKSPACE_APP}
						content={
							<ToggleSettingGroup
								settings={
									organizeByCategoriesConfig.toggleOptions?.map((subSetting, index) => ({
										settingId: subSetting.id,
										title: subSetting.title,
										checked:
											index === 0
												? settings.organizeByCategories.separateFoldersInStartMenu
												: settings.organizeByCategories.separateFoldersInDesktop,
										onChange:
											index === 0
												? handleStartMenuCategoriesChange
												: handleDesktopCategoriesChange,
									})) || []
								}
							/>
						}
					/>
				</div>
			}
		/>
	);
};
